/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Fragment, useEffect } from 'react';
import PropTypes from "prop-types";
import classnames from 'classnames';
import { keys, EuiWindowEvent } from '../../services';
import { keysOf } from '../common';
import { EuiFocusTrap } from '../focus_trap';
import { EuiOverlayMask } from '../overlay_mask';
import { EuiButtonIcon } from '../button';
import { EuiI18n } from '../i18n';
var sizeToClassNameMap = {
  s: 'euiFlyout--small',
  m: 'euiFlyout--medium',
  l: 'euiFlyout--large'
};
var paddingSizeToClassNameMap = {
  none: 'euiFlyout--paddingNone',
  s: 'euiFlyout--paddingSmall',
  m: 'euiFlyout--paddingMedium',
  l: 'euiFlyout--paddingLarge'
};
export var PADDING_SIZES = keysOf(paddingSizeToClassNameMap);
export var EuiFlyout = ({
  className,
  children,
  hideCloseButton = false,
  onClose,
  ownFocus = false,
  size = 'm',
  paddingSize = 'l',
  closeButtonAriaLabel,
  maxWidth = false,
  style,
  maskProps,
  ...rest
}) => {
  const onKeyDown = event => {
    if (event.key === keys.ESCAPE) {
      event.preventDefault();
      onClose();
    }
  };

  useEffect(() => {
    document.body.classList.add('euiBody--hasFlyout');
    return () => {
      document.body.classList.remove('euiBody--hasFlyout');
    };
  });
  let newStyle;
  let widthClassName;

  if (maxWidth === true) {
    widthClassName = 'euiFlyout--maxWidth-default';
  } else if (maxWidth !== false) {
    const value = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
    newStyle = { ...style,
      maxWidth: value
    };
  }

  const classes = classnames('euiFlyout', sizeToClassNameMap[size], paddingSizeToClassNameMap[paddingSize], widthClassName, className);
  let closeButton;

  if (onClose && !hideCloseButton) {
    closeButton = <EuiI18n token="euiFlyout.closeAriaLabel" default="Close this dialog">
        {closeAriaLabel => <EuiButtonIcon className="euiFlyout__closeButton" iconType="cross" color="text" aria-label={closeButtonAriaLabel || closeAriaLabel} onClick={() => {
        onClose();
      }} data-test-subj="euiFlyoutCloseButton" />}
      </EuiI18n>;
  }

  const flyoutContent = <div role="dialog" className={classes} tabIndex={0} style={newStyle || style} {...rest}>
      {closeButton}
      {children}
    </div>; // If ownFocus is set, show an overlay behind the flyout and allow the user
  // to click it to close it.

  let optionalOverlay;

  if (ownFocus) {
    optionalOverlay = <EuiOverlayMask onClick={onClose} headerZindexLocation="below" {...maskProps} />;
  }

  return <Fragment>
      <EuiWindowEvent event="keydown" handler={onKeyDown} />
      {optionalOverlay}
      {
      /*
      * Trap focus even when `ownFocus={false}`, otherwise closing
      * the flyout won't return focus to the originating button.
      *
      * Set `clickOutsideDisables={true}` when `ownFocus={false}`
      * to allow non-keyboard users the ability to interact with
      * elements outside the flyout.
      */
    }
      <EuiFocusTrap clickOutsideDisables={!ownFocus}>
        {flyoutContent}
      </EuiFocusTrap>
    </Fragment>;
};
EuiFlyout.propTypes = {
  onClose: PropTypes.func.isRequired,

  /**
     * Defines the width of the panel
     */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
     * Customize the padding around the content of the flyout header, body and footer
     */
  paddingSize: PropTypes.any,

  /**
     * Hides the default close button. You must provide another close button somewhere within the flyout.
     */
  hideCloseButton: PropTypes.bool,

  /**
     * Adds an EuiOverlayMask when set to `true`
     */
  ownFocus: PropTypes.bool,

  /**
     * Specify an aria-label for the close button of the flyout.
     * Default is `'Close this dialog'`.
     */
  closeButtonAriaLabel: PropTypes.string,

  /**
     * Sets the max-width of the panel,
     * set to `true` to use the default size,
     * set to `false` to not restrict the width,
     * set to a number for a custom width in px,
     * set to a string for a custom width in custom measurement.
     */
  maxWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired]),
  style: PropTypes.any,

  /**
     * Adjustments to the EuiOverlayMask that is added when `ownFocus = true`
     */
  maskProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,

    /**
       * Function that applies to clicking the mask itself and not the children
       */
    onClick: PropTypes.func,

    /**
       * ReactNode to render as this component's content
       */
    children: PropTypes.node,

    /**
       * Should the mask visually sit above or below the EuiHeader (controlled by z-index)
       */
    headerZindexLocation: PropTypes.oneOf(["above", "below"])
  }),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiFlyout.__docgenInfo = {
    description: '',
    displayName: 'EuiFlyout',
    methods: [],
    props: {
      onClose: {
        defaultValue: null,
        description: '',
        name: 'onClose',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: true,
        type: {
          name: '() => void'
        }
      },
      size: {
        defaultValue: {
          value: 'm'
        },
        description: 'Defines the width of the panel',
        name: 'size',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      paddingSize: {
        defaultValue: {
          value: 'l'
        },
        description: 'Customize the padding around the content of the flyout header, body and footer',
        name: 'paddingSize',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "s" | "m" | "l"',
          value: [{
            value: '"none"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      hideCloseButton: {
        defaultValue: {
          value: 'false'
        },
        description: 'Hides the default close button. You must provide another close button somewhere within the flyout.',
        name: 'hideCloseButton',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      ownFocus: {
        defaultValue: {
          value: 'false'
        },
        description: 'Adds an EuiOverlayMask when set to `true`',
        name: 'ownFocus',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'boolean'
        }
      },
      closeButtonAriaLabel: {
        defaultValue: null,
        description: 'Specify an aria-label for the close button of the flyout.\nDefault is `\'Close this dialog\'`.',
        name: 'closeButtonAriaLabel',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      maxWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Sets the max-width of the panel,\nset to `true` to use the default size,\nset to `false` to not restrict the width,\nset to a number for a custom width in px,\nset to a string for a custom width in custom measurement.',
        name: 'maxWidth',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'boolean | number | string'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      maskProps: {
        defaultValue: null,
        description: 'Adjustments to the EuiOverlayMask that is added when `ownFocus = true`',
        name: 'maskProps',
        parent: {
          fileName: 'app/src/components/flyout/flyout.tsx',
          name: 'EuiFlyoutProps'
        },
        required: false,
        type: {
          name: 'EuiOverlayMaskProps'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiFlyoutProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}